import React, { useState, useEffect } from 'react';
import { AuditFiltersComponent, AuditActivitiesList } from './index';
import { getAuditActivities } from '../../../../data/mockAuditData';
import type { AuditActivity, AuditFilters as AuditFiltersType } from '../../../../types/auditLogs';

interface AuditLogsProps {
  className?: string;
}

const AuditLogs: React.FC<AuditLogsProps> = ({ className = '' }) => {
  const [activities, setActivities] = useState<AuditActivity[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<AuditActivity[]>([]);
  const [filters, setFilters] = useState<AuditFiltersType>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    const loadAuditActivities = async () => {
      try {
        setIsLoading(true);
        const data = await getAuditActivities();
        setActivities(data);
        setFilteredActivities(data);
      } catch (err) {
        setError('Failed to load audit activities');
        console.error('Error loading audit activities:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuditActivities();
  }, []);

  // Apply filters when filters change
  useEffect(() => {
    let filtered = [...activities];

    // Apply date range filter
    if (filters.fromDate) {
      filtered = filtered.filter(activity => 
        activity.timestamp >= filters.fromDate!
      );
    }

    if (filters.toDate) {
      // Set to end of day for toDate
      const endOfDay = new Date(filters.toDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(activity => 
        activity.timestamp <= endOfDay
      );
    }

    // Apply activity type filter
    if (filters.activityType && filters.activityType.length > 0) {
      filtered = filtered.filter(activity => 
        filters.activityType!.includes(activity.type)
      );
    }

    // Apply user filter
    if (filters.user) {
      filtered = filtered.filter(activity => 
        activity.user.toLowerCase().includes(filters.user!.toLowerCase())
      );
    }

    setFilteredActivities(filtered);
  }, [activities, filters]);

  const handleFiltersChange = (newFilters: AuditFiltersType) => {
    setFilters(newFilters);
  };

  if (error) {
    return (
      <div className={`audit-logs ${className}`}>
        <div className="audit-error">
          <p>Error: {error}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`audit-logs ${className}`}>
      <AuditFiltersComponent
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      <AuditActivitiesList
        activities={filteredActivities}
        isLoading={isLoading}
      />
    </div>
  );
};

export default AuditLogs;